import { useContext, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import VicinityContext from "../context/vicinityContext"
import { useAuth } from "../hooks/useAuth"
import { readNotification } from "../services/chaperonConnector"
import {
    notificationActionLabel,
    notificationActionPath,
    recentUnreadNotifications,
} from "../utilities/notifications"
import { NOTIFICATION_PREVIEW_COUNT } from "../const/numbers"
import "./DashboardNotificationsCard.css"

const TYPE_ICONS = {
    alert: "bi-exclamation-triangle",
    invitation: "bi-envelope",
}

const formatTimestamp = (value) => {
    const date = new Date(value)
    if (!value || Number.isNaN(date.getTime())) return ""
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    })
}

/**
 * Recent-activity preview of the signed-in user's unread notifications, on the
 * home dashboard. Modeled on the Android Dashboard's notifications card: a
 * count badge, the 3 newest unread items each with a call to action and "Mark
 * as read", and "View all" to open the full notification center. Renders
 * nothing when there is nothing unread.
 */
export default function DashboardNotificationsCard() {
    const navigate = useNavigate()
    const isLoggedIn = useAuth()
    const { notifications, userId, fetchNotifications, setShowNotificationModal } =
        useContext(VicinityContext)
    // Ids marked read here, hidden at once instead of waiting on the refetch
    const [hiddenIds, setHiddenIds] = useState(() => new Set())

    const unread = useMemo(
        () =>
            recentUnreadNotifications(notifications).filter(
                (notification) => !hiddenIds.has(notification.id),
            ),
        [notifications, hiddenIds],
    )

    if (!isLoggedIn || unread.length === 0) return null

    const markRead = async (notification) => {
        setHiddenIds((current) => new Set(current).add(notification.id))
        try {
            await readNotification(userId, notification.id)
        } catch (error) {
            console.error("Error marking notification as read:", error)
        }
        fetchNotifications?.()
    }

    // Acting on a notification implies reading it. A notification with no page
    // of its own opens the notification center, where it stays for reading.
    const runAction = (notification) => {
        const path = notificationActionPath(notification)
        if (path) {
            markRead(notification)
            navigate(path)
        } else {
            setShowNotificationModal(true)
        }
    }

    return (
        <section
            className="dashboard-notifications text-start"
            aria-labelledby="dashboard-notifications-title"
        >
            <div className="dashboard-notifications-header">
                <span
                    className="dashboard-notifications-count"
                    aria-label={`${unread.length} unread`}
                >
                    {unread.length}
                </span>
                <h2 id="dashboard-notifications-title">Notifications</h2>
            </div>

            <ul className="dashboard-notifications-list">
                {unread.slice(0, NOTIFICATION_PREVIEW_COUNT).map((notification) => (
                    <li key={notification.id} className="dashboard-notification">
                        {formatTimestamp(notification.created_at) && (
                            <div className="dashboard-notification-time">
                                {formatTimestamp(notification.created_at)}
                            </div>
                        )}
                        <div className="dashboard-notification-main">
                            <span
                                className="dashboard-notification-icon"
                                aria-hidden="true"
                            >
                                <i
                                    className={`bi ${
                                        TYPE_ICONS[notification.type] ||
                                        "bi-info-circle"
                                    }`}
                                ></i>
                            </span>
                            <div className="dashboard-notification-text">
                                <div className="dashboard-notification-title">
                                    {notification.title || "Notification"}
                                </div>
                                {notification.body && (
                                    <div className="dashboard-notification-body">
                                        {notification.body}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="dashboard-notification-actions">
                            <button
                                type="button"
                                className="btn btn-link btn-sm fw-bold text-decoration-none"
                                onClick={() => runAction(notification)}
                            >
                                {notificationActionLabel(notification)}
                            </button>
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-secondary text-decoration-none"
                                onClick={() => markRead(notification)}
                            >
                                <i className="bi bi-check2 me-1"></i>
                                Mark as read
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            <div className="dashboard-card-footer">
                <button
                    type="button"
                    className="dashboard-card-footer-button"
                    onClick={() => setShowNotificationModal(true)}
                >
                    View all
                    <i className="bi bi-chevron-right ms-1"></i>
                </button>
            </div>
        </section>
    )
}
